import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Facebook, ArrowUp, Mail, Phone, MapPin } from "lucide-react";
import { contactInfo } from "../data/products";
import bambooO from "../assets/bamboo-o.png";

function FooterLogo() {
  return (
    <div className="flex flex-col items-start">
      <div className="flex items-center" style={{ letterSpacing: '0', lineHeight: 1 }}>
        <span 
          className="text-2xl md:text-3xl text-[#E8E8E8] font-bold"
          style={{ fontFamily: "'DM Serif Display', serif", letterSpacing: '-0.01em' }}
        >PR</span>
        <img 
          src={bambooO} 
          alt="O" 
          className="h-7 md:h-9 w-auto inline-block"
          style={{ margin: '0 -3px', verticalAlign: 'middle' }}
        />
        <span 
          className="text-2xl md:text-3xl text-[#E8E8E8] font-bold"
          style={{ fontFamily: "'DM Serif Display', serif", letterSpacing: '-0.01em' }}
        >BWA</span>
      </div>
      <span className="text-[#8BC34A] text-xs tracking-[0.2em] font-light mt-1">
        ECO BOIS CONCEPT
      </span>
    </div>
  );
}

export function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentYear = new Date().getFullYear();

  const footerLinks = [
    {
      title: "Produits",
      links: [
        { name: "Terrasses", href: "/nos-produits" },
        { name: "Bardage", href: "/nos-produits" },
        { name: "Tasseaux", href: "/nos-produits" },
        { name: "Parquets", href: "/nos-produits" },
      ],
    },
    {
      title: "Navigation",
      links: [
        { name: "Accueil", href: "/" },
        { name: "Le Bambou", href: "/le-bambou" },
        { name: "Nos Produits", href: "/nos-produits" },
        { name: "Calculateur", href: "/calculateur" },
        { name: "Demande de devis", href: "/demande-de-devis" },
      ],
    },
    {
      title: "Legal",
      links: [
        { name: "Mentions légales", href: "/mentions-legales" },
        { name: "Confidentialité", href: "/politique-confidentialite" },
        { name: "CGV", href: "/cgv" },
      ],
    },
  ];

  return (
    <footer className="bg-[#2D2D2D] text-[#E8E8E8]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="py-16 md:py-20 grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <FooterLogo />
            </Link>
            <p className="text-[#9E9E9E] mb-6 leading-relaxed">
              Distributeur REBO Bamboo à La Réunion. 
              Terrasses, bardages, parquets et revêtements muraux en bambou tressé carbonisé.
            </p>
            
            <div className="space-y-3 mb-6">
              <a 
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-2 text-[#9E9E9E] hover:text-[#8BC34A] transition-colors"
              >
                <Mail className="w-4 h-4 text-[#8BC34A]" />
                {contactInfo.email}
              </a>
              <a 
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-2 text-[#9E9E9E] hover:text-[#8BC34A] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#8BC34A]" />
                {contactInfo.phone}
              </a>
              <div className="flex items-center gap-2 text-[#9E9E9E]">
                <MapPin className="w-4 h-4 text-[#8BC34A]" />
                Saint-Paul, La Réunion
              </div>
            </div>

            <div className="flex items-center gap-4">
              {[
                { icon: Facebook, href: "https://www.facebook.com/probwa974", label: "Facebook" },
              ].map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#3A3A3A] flex items-center justify-center hover:bg-[#8BC34A] transition-all duration-300 group"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5 text-[#8BC34A] group-hover:text-[#2D2D2D] transition-colors" />
                </motion.a>
              ))}
            </div>
          </div>

          {footerLinks.map((column) => (
            <div key={column.title}>
              <h4 className="font-semibold text-[#8BC34A] mb-6 text-sm tracking-widest uppercase">
                {column.title}
              </h4>
              <ul className="space-y-4">
                {column.links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-[#9E9E9E] hover:text-[#E8E8E8] transition-colors duration-300"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="py-6 border-t border-[#3A3A3A] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-[#9E9E9E]">
            {currentYear} ProBwa - Eco Bois Concept. Tous droits réservés.
          </p>
          <p className="text-xs text-[#9E9E9E]">
            R.C.S. 982 037 558 Saint Denis de La Réunion
          </p>
          <motion.button
            onClick={scrollToTop}
            className="w-10 h-10 rounded-full bg-[#8BC34A] flex items-center justify-center hover:bg-[#A5D66A] transition-all duration-300"
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Retour en haut"
            data-testid="scroll-to-top"
          >
            <ArrowUp className="w-5 h-5 text-[#2D2D2D]" />
          </motion.button>
        </div>
      </div>
    </footer>
  );
}
